/**
 * useActivityHydration — current URL 이 ActivityHost 가 흡수한 sub-activity 인데
 * store 가 비어있으면, URL → store 로 채워준다.
 *
 * Deep-link 진입 (브라우저 주소창에 /shop/1 직접 입력) 또는 새로고침 시 필요.
 * 일반 in-app 네비 (useOverlayNavigate → activityNav.open) 는 이미 store 를
 * 채우므로 이 훅은 no-op.
 *
 * RootLayout 에서 한 번만 마운트.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useActivityStore } from "../store/activityStore";
import { getActivityRouteRule } from "./activityRoutes";
import {
  ACTIVITY_HOST_HANDLED_KINDS,
  AUTH_ACTIVITY_FALLBACK_PATH,
} from "../components/layout/ActivityHost";
import { inferMainTabKindFromPath, type MainTabKind } from "../components/layout/mainTabRegistry";

/**
 * Auth flow URL prefixes — deep-link 직접 진입 시 auth activity 로 hydrate.
 * 동일 URL 이 SubActivity (SUB_ACTIVITY_RESOLVERS) 에는 등록 안 돼있고 ActivityHost
 * 가 별도 슬롯으로 처리하므로 ACTIVITY_HOST_HANDLED_KINDS 검사를 우회한다.
 */
const AUTH_FLOW_PATHS = ["/login"] as const;

function isAuthFlowPath(pathname: string): boolean {
  return AUTH_FLOW_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/**
 * Main tab kind → 그 탭의 URL path.
 * Hydration 시 호출자 컨텍스트를 모르므로 inferred main tab path 를 fallback 으로 사용.
 */
const MAIN_TAB_PATH: Record<MainTabKind, string> = {
  home: "/",
  search: "/search",
  collections: "/collections",
  me: "/me",
  notifications: "/notifications",
};

export function useActivityHydration(): void {
  const location = useLocation();
  const url = location.pathname + location.search;

  useEffect(() => {
    // === Auth flow deep-link hydration ===
    // /login 등 직접 URL 진입 시 auth activity 가 store 에 없으면 push.
    // useRequireAuth / RequireAuth 가 정상 흐름으로 띄운 경우엔 이미 store 에
    // 있으므로 동일 URL 이면 skip.
    if (isAuthFlowPath(location.pathname)) {
      const state = useActivityStore.getState();
      const authActivity = state.activities.find((a) => a.kind === "auth");
      const authTopEntryUrl =
        authActivity?.entries[authActivity.entries.length - 1]?.url ?? null;
      if (authTopEntryUrl === url) return;
      if (!authActivity) {
        state.openActivity({
          kind: "auth",
          presentation: "fullscreen",
          appBarAction: "close",
          url,
          fallbackPath: AUTH_ACTIVITY_FALLBACK_PATH,
        });
      } else {
        state.pushEntry({ url });
      }
      return;
    }

    const rule = getActivityRouteRule(location.pathname);
    if (!rule || !ACTIVITY_HOST_HANDLED_KINDS.has(rule.kind)) return;

    const state = useActivityStore.getState();
    const top = state.activities[state.activities.length - 1];
    // 이미 같은 URL 의 activity 가 top 에 있으면 skip — in-app 네비가 이미 채움.
    if (top?.kind === rule.kind && top.entries[top.entries.length - 1]?.url === url) {
      return;
    }

    // 호출자가 누군지 모르므로 (deep-link, browser-back, refresh) URL 의 main tab 을
    // 추론해서 fallback 으로 사용. rule.fallbackPath 는 이미 currentPathname=undefined
    // 로 계산된 (잘못된) 기본값이므로 무시.
    const inferredMainTab = inferMainTabKindFromPath(location.pathname);
    const fallbackPath = inferredMainTab ? MAIN_TAB_PATH[inferredMainTab] : rule.fallbackPath;

    state.openActivity({
      kind: rule.kind,
      presentation: rule.presentation,
      appBarAction: rule.appBarAction,
      url,
      fallbackPath,
      state: rule.state,
    });
  }, [location.pathname, location.search, url]);
}
